import { useEffect, useRef, useState } from 'react';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { get } from '@/api/client';
import { ServiceNav } from '@/components/organisms/ServiceNav';
import { SEOHead } from '@/components/atoms/SEOHead';
import { RealEstateTrendChart } from '@/components/charts/RealEstateTrendChart';
import { Skeleton } from '@/components/atoms/Skeleton';

/**
 * `/home/coreawhale/references/full-map-v2.html` 레퍼런스 그대로: Leaflet + OpenStreetMap,
 * 다크 타일 필터, 줌 레벨(z<=7 전국 / 8~9 광역 / 10+ 동네) 기반 3단계 레이어 전환.
 * 데이터는 카카오맵 버전(RealEstateStatusPage)과 동일한 실제 국토부 실거래가 API 재사용 —
 * 지도 라이브러리만 다르다.
 *
 * 동(洞) 좌표는 apt_complex_coords(Kakao Local, 현재 서비스 이슈로 보류) 대신
 * dong_coords(Nominatim/OSM 직접 지오코딩)를 사용 — 이 페이지가 Kakao 의존성 없는
 * OSM 전용 버전이라는 원래 취지를 유지한다.
 */

interface RegionSummary {
  code: string;
  name: string;
  level: 'sido' | 'sigungu';
  parentCode: string | null;
  lat: number | null;
  lng: number | null;
  avgPricePerM2: number | null;
  changeRate: number | null;
  dealCount: number;
}

interface DongSummary {
  dong: string;
  lat: number | null;
  lng: number | null;
  avgPricePerM2: number | null;
  changeRate: number | null;
  dealCount: number;
}

interface MapBoundsState {
  swLat: number;
  swLng: number;
  neLat: number;
  neLng: number;
}

interface RecentDeal {
  apt_name: string;
  dong: string;
  area_m2: string;
  floor: number;
  build_year: number | null;
  deal_amount: string;
  deal_ymd: string;
}

const CENTER: [number, number] = [36.5, 127.7];
const INITIAL_ZOOM = 7;

/** 레퍼런스 chgColor 그대로 이식 — 7단계 그라데이션 */
function chgColor(v: number | null, op = 0.72): string {
  if (v === null) return `rgba(130,145,170,${op})`;
  if (v >= 2) return `rgba(170,25,25,${op})`;
  if (v >= 1) return `rgba(215,50,50,${op})`;
  if (v >= 0.3) return `rgba(245,130,90,${op})`;
  if (v >= -0.3) return `rgba(130,145,170,${op})`;
  if (v >= -1) return `rgba(90,140,225,${op})`;
  if (v >= -2) return `rgba(55,95,205,${op})`;
  return `rgba(30,55,175,${op})`;
}

function fmtC(v: number | null): string {
  if (v === null) return '-';
  return `${v > 0 ? '+' : ''}${v.toFixed(1)}%`;
}

function formatPrice(v: number | null): string {
  if (v === null) return '데이터 없음';
  return `${(v / 10000).toFixed(1)}억/㎡`;
}

function formatDealAmount(v: string): string {
  const n = Number(v);
  if (!Number.isFinite(n)) return '-';
  const eok = Math.floor(n / 10000);
  const rest = n % 10000;
  return rest ? `${eok}억 ${Math.round(rest / 1000)}천만` : `${eok}억`;
}

function regionLabelIcon(name: string, changeRate: number | null, scale: 'lg' | 'md' | 'sm'): L.DivIcon {
  const nameSize = scale === 'lg' ? 12 : scale === 'md' ? 10 : 9;
  const pctSize = scale === 'lg' ? 13 : scale === 'md' ? 11 : 10;
  const w = scale === 'lg' ? 90 : scale === 'md' ? 80 : 72;
  const h = scale === 'lg' ? 36 : scale === 'md' ? 32 : 28;
  const pctColor = changeRate !== null && changeRate >= 0 ? '#ffbbbb' : '#bbddff';
  return L.divIcon({
    html: `<div style="text-align:center;pointer-events:none;white-space:nowrap"><div style="font-size:${nameSize}px;font-weight:800;color:#fff;text-shadow:0 1px 4px rgba(0,0,0,.9)">${name}</div><div style="font-size:${pctSize}px;font-weight:900;color:${pctColor};text-shadow:0 1px 4px rgba(0,0,0,.9)">${fmtC(changeRate)}</div></div>`,
    className: '',
    iconSize: [w, h],
    iconAnchor: [w / 2, h / 2],
  });
}

export function RealEstateStatusOtherPage() {
  const mapContainerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<L.Map | null>(null);
  const sidoLayerRef = useRef<L.LayerGroup | null>(null);
  const sigunguLayerRef = useRef<L.LayerGroup | null>(null);
  const dongLayerRef = useRef<L.LayerGroup | null>(null);
  const [level, setLevel] = useState<'sido' | 'sigungu' | 'dong'>('sido');
  const [selectedCode, setSelectedCode] = useState<string | null>(null);
  const [selectedDong, setSelectedDong] = useState<{ parentCode: string; dong: string } | null>(null);
  const [mapBounds, setMapBounds] = useState<MapBoundsState | null>(null);
  const boundsDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const sidoQuery = useQuery({
    queryKey: ['realestate', 'regions', 'sido'],
    queryFn: () => get<{ regions: RegionSummary[] }>('/real-estate/regions?level=sido'),
    staleTime: 10 * 60_000,
  });

  /* 시군구는 전국 250여개라 뷰포트(bounds) 없이 한번에 다 그리면 밀집 지역(수도권 등)에서
     원이 서로 겹쳐 라벨을 읽을 수 없다 — 현재 지도 화면 안의 시군구만 조회한다. level 로 게이팅하지
     않고 항상 bounds 기준으로 조회해둔다 — 아래 dong 레벨에서 "가장 가까운 시군구" 계산에 재사용. */
  const sigunguQuery = useQuery({
    queryKey: ['realestate', 'regions', 'sigungu', mapBounds],
    queryFn: () =>
      get<{ regions: RegionSummary[] }>(
        `/real-estate/regions?level=sigungu&swLat=${mapBounds!.swLat}&swLng=${mapBounds!.swLng}&neLat=${mapBounds!.neLat}&neLng=${mapBounds!.neLng}`,
      ),
    enabled: !!mapBounds,
    placeholderData: keepPreviousData,
    staleTime: 60_000,
  });

  /* 동(洞) 레벨은 특정 시/군/구에 종속된 데이터라 부모 코드가 필요 — 레퍼런스처럼 클릭으로 명시
     선택하는 대신, 사용자가 원한 "줌 레벨에 따라 자동 전환" 설계에 맞춰 현재 화면 중심에서 가장
     가까운 시/군/구를 자동으로 찾아 그 산하 동만 보여준다. */
  const nearestSigunguCode =
    level === 'dong' && mapBounds
      ? (() => {
          const regions = sigunguQuery.data?.regions ?? [];
          const centerLat = (mapBounds.swLat + mapBounds.neLat) / 2;
          const centerLng = (mapBounds.swLng + mapBounds.neLng) / 2;
          let best: string | null = null;
          let bestDist = Infinity;
          for (const r of regions) {
            if (r.lat === null || r.lng === null) continue;
            const dist = (r.lat - centerLat) ** 2 + (r.lng - centerLng) ** 2;
            if (dist < bestDist) {
              bestDist = dist;
              best = r.code;
            }
          }
          return best;
        })()
      : null;

  const dongQuery = useQuery({
    queryKey: ['realestate', 'dong-summary', nearestSigunguCode],
    queryFn: () => get<{ dongs: DongSummary[] }>(`/real-estate/dong-summary?parent=${nearestSigunguCode}`),
    enabled: !!nearestSigunguCode,
    placeholderData: keepPreviousData,
    staleTime: 60_000,
  });

  const regions = level === 'sido' ? sidoQuery.data?.regions ?? [] : sigunguQuery.data?.regions ?? [];
  const selectedRegion = regions.find((r) => r.code === selectedCode) ?? null;
  const selectedDongSummary = selectedDong
    ? dongQuery.data?.dongs.find((d) => d.dong === selectedDong.dong) ?? null
    : null;
  const selectedPanel = selectedDongSummary
    ? {
        name: selectedDongSummary.dong,
        changeRate: selectedDongSummary.changeRate,
        avgPricePerM2: selectedDongSummary.avgPricePerM2,
        dealCount: selectedDongSummary.dealCount,
      }
    : selectedRegion;

  const dealsTarget = selectedDong
    ? { code: selectedDong.parentCode, dong: selectedDong.dong }
    : selectedCode
    ? { code: selectedCode, dong: undefined as string | undefined }
    : null;

  const { data: dealData, isLoading: dealsLoading } = useQuery({
    queryKey: ['realestate', 'deals', dealsTarget?.code, dealsTarget?.dong],
    queryFn: () =>
      get<{ trend: { ym: string; avgPrice: number | null; dealCount: number }[]; recentDeals: RecentDeal[] }>(
        `/real-estate/regions/${dealsTarget!.code}/deals?months=6${
          dealsTarget!.dong ? `&dong=${encodeURIComponent(dealsTarget!.dong)}` : ''
        }`,
      ),
    enabled: !!dealsTarget,
  });

  /* 시/도·시/군/구·동 원의 클릭 동작은 레이어별로 고정돼 있다(레퍼런스와 동일) — 현재 level state 에
     분기하지 않는다. 만약 분기했다면, 각 레이어 렌더 effect(아래)가 각 쿼리의 data 가 바뀔 때만
     재실행되므로 이후 level 이 바뀌어도 클릭 핸들러 클로저가 그 시점의 옛 level 값을 그대로 참조하는
     stale closure 버그가 생긴다. */
  const handleSidoCircleClick = (region: RegionSummary) => {
    if (region.lat !== null && region.lng !== null) {
      mapRef.current?.flyTo([region.lat, region.lng], 9);
    }
  };

  const handleSigunguCircleClick = (region: RegionSummary) => {
    setSelectedDong(null);
    setSelectedCode(region.code);
  };

  const handleDongCircleClick = (parentCode: string, dong: DongSummary) => {
    setSelectedCode(null);
    setSelectedDong({ parentCode, dong: dong.dong });
  };

  // 지도 최초 생성 (한 번만)
  useEffect(() => {
    if (!mapContainerRef.current || mapRef.current) return;

    const map = L.map(mapContainerRef.current, {
      center: CENTER,
      zoom: INITIAL_ZOOM,
      zoomControl: false,
      attributionControl: false,
    });
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', { maxZoom: 19 }).addTo(map);
    L.control.zoom({ position: 'topright' }).addTo(map);

    const sidoLayer = L.layerGroup().addTo(map);
    const sigunguLayer = L.layerGroup();
    const dongLayer = L.layerGroup();

    const updateLevel = () => {
      const z = map.getZoom();
      setLevel(z <= 7 ? 'sido' : z <= 9 ? 'sigungu' : 'dong');
    };
    map.on('zoomend', updateLevel);

    const updateBounds = () => {
      if (boundsDebounceRef.current) clearTimeout(boundsDebounceRef.current);
      boundsDebounceRef.current = setTimeout(() => {
        const b = map.getBounds();
        setMapBounds({ swLat: b.getSouth(), swLng: b.getWest(), neLat: b.getNorth(), neLng: b.getEast() });
      }, 300);
    };
    map.on('moveend', updateBounds);
    updateBounds();

    mapRef.current = map;
    sidoLayerRef.current = sidoLayer;
    sigunguLayerRef.current = sigunguLayer;
    dongLayerRef.current = dongLayer;

    return () => {
      if (boundsDebounceRef.current) clearTimeout(boundsDebounceRef.current);
      map.remove();
      mapRef.current = null;
    };
  }, []);

  // 시/도 레이어 데이터 렌더 (원 + 라벨)
  useEffect(() => {
    const layer = sidoLayerRef.current;
    if (!layer) return;
    layer.clearLayers();
    for (const r of sidoQuery.data?.regions ?? []) {
      if (r.lat === null || r.lng === null) continue;
      const radius = 18000 + Math.min(r.dealCount, 300) * 60;
      const circle = L.circle([r.lat, r.lng], {
        radius,
        fillColor: chgColor(r.changeRate, 0.65),
        fillOpacity: 0.65,
        color: '#fff',
        weight: 1,
        opacity: 0.25,
      });
      circle.on('click', () => handleSidoCircleClick(r));
      layer.addLayer(circle);
      layer.addLayer(L.marker([r.lat, r.lng], { icon: regionLabelIcon(r.name, r.changeRate, 'lg'), interactive: false }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sidoQuery.data]);

  // 시/군/구 레이어 데이터 렌더
  useEffect(() => {
    const layer = sigunguLayerRef.current;
    if (!layer) return;
    layer.clearLayers();
    for (const r of sigunguQuery.data?.regions ?? []) {
      if (r.lat === null || r.lng === null) continue;
      const radius = 1800 + Math.abs(r.changeRate ?? 0) * 80;
      const circle = L.circle([r.lat, r.lng], {
        radius,
        fillColor: chgColor(r.changeRate, 0.7),
        fillOpacity: 0.7,
        color: '#fff',
        weight: 1.2,
        opacity: 0.3,
      });
      circle.on('click', () => handleSigunguCircleClick(r));
      layer.addLayer(circle);
      layer.addLayer(L.marker([r.lat, r.lng], { icon: regionLabelIcon(r.name, r.changeRate, 'md'), interactive: false }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sigunguQuery.data]);

  // 동(洞) 레이어 데이터 렌더 — 좌표 미확보(geocode_status != 'ok') 동은 건너뛴다
  useEffect(() => {
    const layer = dongLayerRef.current;
    if (!layer) return;
    layer.clearLayers();
    const parentCode = nearestSigunguCode;
    if (!parentCode) return;
    for (const d of dongQuery.data?.dongs ?? []) {
      if (d.lat === null || d.lng === null) continue;
      const radius = 900 + Math.abs(d.changeRate ?? 0) * 40;
      const circle = L.circle([d.lat, d.lng], {
        radius,
        fillColor: chgColor(d.changeRate, 0.75),
        fillOpacity: 0.75,
        color: '#fff',
        weight: 1.2,
        opacity: 0.35,
      });
      circle.on('click', () => handleDongCircleClick(parentCode, d));
      layer.addLayer(circle);
      layer.addLayer(L.marker([d.lat, d.lng], { icon: regionLabelIcon(d.dong, d.changeRate, 'sm'), interactive: false }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dongQuery.data]);

  // 레벨(줌) 전환에 따라 레이어 교체
  useEffect(() => {
    const map = mapRef.current;
    const sido = sidoLayerRef.current;
    const sigungu = sigunguLayerRef.current;
    const dong = dongLayerRef.current;
    if (!map || !sido || !sigungu || !dong) return;
    map.removeLayer(sido);
    map.removeLayer(sigungu);
    map.removeLayer(dong);
    if (level === 'sido') map.addLayer(sido);
    else if (level === 'sigungu') map.addLayer(sigungu);
    else map.addLayer(dong);
  }, [level]);

  const regionsLoading =
    level === 'sido' ? sidoQuery.isLoading : level === 'sigungu' ? sigunguQuery.isLoading : dongQuery.isLoading;

  return (
    <div className="container-base pt-4 pb-6">
      <SEOHead
        title="부동산 현황 (OSM)"
        description="국토교통부 아파트 매매 실거래가 기반 지역별 시세 지도 — OpenStreetMap 버전"
        canonical="/realestate-status-other"
      />
      <ServiceNav />

      <div className="mb-4">
        <h1 className="text-2xl font-extrabold">부동산 현황</h1>
      </div>
      <p className="mb-4 text-sm text-[var(--color-text-sub)]">
        국토교통부 아파트 매매 실거래가 기준 · 전월 대비 ㎡당 평균가 변동률
      </p>

      <style>{`
        .realestate-leaflet-dark.leaflet-container { background: #090c14; }
        .realestate-leaflet-dark .leaflet-tile { filter: brightness(.72) saturate(.55) hue-rotate(188deg); }
        .realestate-leaflet-dark .leaflet-control-zoom a { background: var(--color-surface) !important; color: var(--color-text) !important; border-color: var(--color-border) !important; }
      `}</style>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-[1fr_320px]">
        <div className="relative h-[480px] overflow-hidden rounded-2xl border border-[var(--color-border)]">
          {regionsLoading && <Skeleton className="absolute inset-0 z-10 h-full w-full" />}
          <div ref={mapContainerRef} className="realestate-leaflet-dark h-full w-full" />
          <div className="pointer-events-none absolute bottom-3 left-3 z-[400] rounded-full bg-black/60 px-3 py-1 text-xs font-semibold text-white">
            {level === 'sido' ? '전국' : level === 'sigungu' ? '광역' : '동네'}
          </div>
        </div>

        <div className="space-y-4">
          {!selectedPanel ? (
            <div className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-surface)] p-4 text-sm text-[var(--color-text-sub)]">
              {level === 'sido'
                ? '지역을 클릭하면 확대돼요.'
                : level === 'sigungu'
                ? '지역을 클릭하면 상세 시세를 볼 수 있어요.'
                : '동을 클릭하면 상세 시세를 볼 수 있어요.'}
            </div>
          ) : (
            <>
              <div className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-surface)] p-4">
                <div className="text-lg font-extrabold">{selectedPanel.name}</div>
                <div
                  className="mt-1 text-2xl font-extrabold"
                  style={{
                    color:
                      selectedPanel.changeRate === null
                        ? undefined
                        : selectedPanel.changeRate > 0
                        ? '#d43030'
                        : selectedPanel.changeRate < 0
                        ? '#3b6fd4'
                        : undefined,
                  }}
                >
                  {fmtC(selectedPanel.changeRate)}
                </div>
                <div className="mt-1 text-xs text-[var(--color-text-sub)]">
                  평균 {formatPrice(selectedPanel.avgPricePerM2)} · 이번달 거래 {selectedPanel.dealCount}건
                </div>
              </div>

              {dealsLoading ? (
                <Skeleton className="h-48 w-full" />
              ) : dealData ? (
                <>
                  <RealEstateTrendChart data={dealData.trend} />
                  <div className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-surface)] p-4">
                    <div className="mb-2 text-xs font-bold uppercase tracking-wide text-[var(--color-text-sub)]">
                      최근 거래
                    </div>
                    {dealData.recentDeals.length === 0 ? (
                      <div className="py-4 text-center text-xs text-[var(--color-text-sub)]">
                        최근 거래 내역이 없어요.
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {dealData.recentDeals.slice(0, 10).map((d, i) => (
                          <div
                            key={i}
                            className="flex items-center justify-between border-b border-[var(--color-border)] pb-2 text-xs last:border-0 last:pb-0"
                          >
                            <div>
                              <div className="font-semibold">{d.apt_name}</div>
                              <div className="text-[var(--color-text-sub)]">
                                {d.dong} · {d.area_m2}㎡ · {d.floor}층 · {d.deal_ymd}
                              </div>
                            </div>
                            <div className="font-bold">{formatDealAmount(d.deal_amount)}</div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </>
              ) : null}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
